/** Strip common PHI patterns from free-text before sending to an AI gateway or broadcast. */

const STREET_ADDRESS_RE =
  /\b\d{1,5}\s+(?:[NSEW]\.?\s+)?[A-Za-z0-9.'-]+(?:\s+(?:St|Street|Ave|Avenue|Rd|Road|Dr|Drive|Ln|Lane|Blvd|Boulevard|Ct|Court|Way|Pl|Place|Cir|Circle|Ter|Terrace|Pkwy|Parkway|Hwy|Highway)\.?)(?:\s+(?:Apt|Suite|Ste|Unit|#)\s*[\w-]+)?\b/gi;

const ZIP_CODE_RE = /\b\d{5}(?:-\d{4})?\b/g;

// Two-word Title Case names (e.g. "Mary Johnson")
const FULL_NAME_RE = /\b[A-Z][a-z]{1,}(?:\s+[A-Z][a-z]{1,})+\b/g;

// Single capitalized first names followed by common care-sentence verbs/prepositions
// Catches patterns like "Linda needs", "Robert lives", "My mother Linda is"
const SINGLE_NAME_RE = /\b([A-Z][a-z]{2,})\s+(?:needs?|requires?|lives?|wants?|has|is|was|called?|asked?)\b/g;

function stripStreetAddresses(text: string): string {
  return text.replace(STREET_ADDRESS_RE, "[address redacted]");
}

function stripZips(text: string): string {
  return text.replace(ZIP_CODE_RE, "[zip retained separately]");
}

export function deidentifyClinicalText(text: string | null | undefined, maxLen = 500): string {
  if (!text?.trim()) return "unknown";

  // Order matters: strip addresses first (before name regex can corrupt the tokens),
  // then phone, email, full names, then single first-names in care-sentence context.
  return stripZips(
    stripStreetAddresses(text)
      .replace(/\b\d{3}[-. ]?\d{3}[-. ]?\d{4}\b/g, "[phone redacted]")
      .replace(/[\w.+-]+@[\w.-]+\.\w+/gi, "[email redacted]")
      .replace(FULL_NAME_RE, "[care recipient]")
      .replace(SINGLE_NAME_RE, "[care recipient] $2"),
  )
    .trim()
    .slice(0, maxLen);
}

/** De-identified clinical summary for community broadcast care_needs (no PII). */
export function sanitizeBroadcastCareNeeds(
  acuityNotes: string | null | undefined,
  maxLen = 500,
): string {
  const summary = deidentifyClinicalText(acuityNotes, maxLen);
  return summary === "unknown" ? "Care needs not specified" : summary;
}

/**
 * Convert a raw Sarah chat transcript into a de-identified clinical summary.
 * Strips user/bot labels and identity references; retains care-context phrases.
 */
export function summarizeTranscript(transcript: string | null | undefined, maxLen = 800): string {
  if (!transcript?.trim()) return "unknown";

  const lines = transcript
    .split(/\n+/)
    .map((line) => line.replace(/^(user|assistant|bot|sarah)\s*:\s*/i, "").trim())
    .filter(Boolean);

  const careLines = lines.filter((line) =>
    /care|help|bath|meal|medication|companion|dementia|alzheimer|mobility|urgent|hour|payer|insurance|medicaid|private|recovery|condition/i.test(line),
  );

  const source = careLines.length > 0 ? careLines.join(" ") : lines.join(" ");
  return deidentifyClinicalText(source, maxLen);
}

export function buildEnrichmentPrompt(lead: {
  zip_code?: string | null;
  acuity_notes?: string | null;
  transcript?: string | null;
  urgency?: number | null;
  hours_needed?: number | null;
  payer_source?: string | null;
}): string {
  const fromNotes = deidentifyClinicalText(lead.acuity_notes);
  const fromTranscript = summarizeTranscript(lead.transcript);
  const clinicalSummary =
    fromNotes !== "unknown" ? fromNotes : fromTranscript !== "unknown" ? fromTranscript : "unknown";

  return `Analyze this home care lead and identify high-value signals:
ZIP: ${lead.zip_code || "unknown"}
Care Needs (de-identified clinical summary): ${clinicalSummary}
Urgency: ${lead.urgency ?? "unknown"}/5
Hours/week: ${lead.hours_needed ?? "unknown"}
Payer Source: ${lead.payer_source || "unknown"}

Return enrichment signals. Do not infer or request personal identifiers.`;
}

export function extractEmailFromText(text: string | null | undefined): string | null {
  if (!text) return null;
  const m = text.match(/[\w.+-]+@[\w.-]+\.\w{2,}/i);
  return m ? m[0].toLowerCase() : null;
}
